import {
  Children,
  createContext,
  createElement,
  forwardRef,
  isValidElement,
  useContext,
  useId,
  useRef,
  useState,
  type FC,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import styles from './list.module.css'

export interface ListProps extends HTMLAttributes<HTMLUListElement> {
  dense?: boolean
  disablePadding?: boolean
}

export const List = forwardRef<HTMLUListElement, ListProps>(
  ({ dense, disablePadding, className, style, ...rest }, ref) => {
    const classes = [styles.list, dense ? styles.dense : undefined, className].filter(Boolean).join(' ')

    return <ul ref={ref} className={classes} style={disablePadding ? { ...style, padding: 0 } : style} {...rest} />
  }
)

List.displayName = 'List'

export interface ListItemProps extends HTMLAttributes<HTMLLIElement> {
  dense?: boolean
  button?: boolean
  selected?: boolean
  disabled?: boolean
  disablePadding?: boolean
  disableGutters?: boolean
  secondaryAction?: ReactNode
  alignItems?: 'flex-start' | 'center'
  /** @deprecated Ignored - kept for MUI compat */
  autoFocus?: boolean
  /** @deprecated Ignored - kept for MUI compat */
  onClose?: () => void
  /** @deprecated Ignored - kept for MUI compat */
  component?: string
}

export const ListItem = forwardRef<HTMLLIElement, ListItemProps>(
  (
    {
      dense,
      button,
      selected,
      disabled,
      disablePadding,
      disableGutters,
      secondaryAction,
      alignItems,
      autoFocus: _autoFocus,
      onClose: _onClose,
      component: _component,
      className,
      style,
      ...rest
    },
    ref
  ) => {
    const classes = [
      styles.item,
      dense ? styles.itemDense : undefined,
      button ? styles.itemButton : undefined,
      selected ? styles.itemSelected : undefined,
      className,
    ]
      .filter(Boolean)
      .join(' ')

    const computedStyle = {
      ...style,
      ...(disablePadding ? { padding: 0 } : undefined),
      ...(disableGutters ? { paddingLeft: 0, paddingRight: 0 } : undefined),
      ...(disabled ? { opacity: 0.38, pointerEvents: 'none' as const } : undefined),
      ...(secondaryAction ? { paddingRight: 48, position: 'relative' as const } : undefined),
      ...(alignItems ? { alignItems } : undefined),
    }

    return (
      <li ref={ref} className={classes} style={computedStyle} {...rest}>
        {rest.children}
        {secondaryAction ? <div className={styles.itemSecondaryAction}>{secondaryAction}</div> : null}
      </li>
    )
  }
)

ListItem.displayName = 'ListItem'

export interface ListItemIconProps extends HTMLAttributes<HTMLDivElement> {}

export const ListItemIcon: FC<ListItemIconProps> = ({ className, ...rest }) => (
  <div className={[styles.itemIcon, className].filter(Boolean).join(' ')} {...rest} />
)

export interface ListItemAvatarProps extends HTMLAttributes<HTMLDivElement> {}

export const ListItemAvatar: FC<ListItemAvatarProps> = ({ className, ...rest }) => (
  <div className={[styles.itemAvatar, className].filter(Boolean).join(' ')} {...rest} />
)

export interface ListItemTextProps extends HTMLAttributes<HTMLDivElement> {
  primary?: ReactNode
  secondary?: ReactNode
  /** Add left padding to align with items that have an icon */
  inset?: boolean
  /** Truncate primary text */
  primaryTypographyProps?: { noWrap?: boolean; style?: React.CSSProperties; fontSize?: number | string }
  secondaryTypographyProps?: { style?: React.CSSProperties }
}

export const ListItemText: FC<ListItemTextProps> = ({
  primary,
  secondary,
  inset,
  primaryTypographyProps,
  secondaryTypographyProps,
  className,
  children,
  style,
  ...rest
}) => {
  const primaryStyle = {
    ...(primaryTypographyProps?.noWrap
      ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }
      : undefined),
    ...(primaryTypographyProps?.fontSize ? { fontSize: primaryTypographyProps.fontSize } : undefined),
    ...primaryTypographyProps?.style,
  }
  return (
    <div
      className={[styles.itemText, className].filter(Boolean).join(' ')}
      style={inset ? { ...style, paddingLeft: 40 } : style}
      {...rest}
    >
      {primary != null ? (
        <span className={styles.itemTextPrimary} style={Object.keys(primaryStyle).length ? primaryStyle : undefined}>
          {primary}
        </span>
      ) : children != null ? (
        // Treat `children` as the primary slot when no `primary` prop is
        // supplied. Without this branch, children render raw and inherit
        // the parent's font-size (e.g. menu items render at body2 instead
        // of the canonical body1 the .itemTextPrimary class applies),
        // producing visibly-smaller text for any caller that uses the
        // `<ListItemText>label</ListItemText>` shorthand.
        <span className={styles.itemTextPrimary} style={Object.keys(primaryStyle).length ? primaryStyle : undefined}>
          {children}
        </span>
      ) : null}
      {secondary != null ? (
        <span className={styles.itemTextSecondary} style={secondaryTypographyProps?.style}>
          {secondary}
        </span>
      ) : null}
      {primary != null ? children : null}
    </div>
  )
}

export interface ListItemSecondaryActionProps extends HTMLAttributes<HTMLDivElement> {}

export const ListItemSecondaryAction: FC<ListItemSecondaryActionProps> = ({ className, ...rest }) => (
  <div className={[styles.itemSecondaryAction, className].filter(Boolean).join(' ')} {...rest} />
)

/** Marks a ListItemButton row. Disclosure keyboard handling finds rows by this, not by an ARIA role. */
const listRowAttr = 'data-list-row'

export interface ListItemButtonProps extends HTMLAttributes<HTMLDivElement> {
  selected?: boolean
  disabled?: boolean
  dense?: boolean
  /** @deprecated Ignored - kept for MUI compat */
  disableRipple?: boolean
}

export const ListItemButton = forwardRef<HTMLDivElement, ListItemButtonProps>(
  (
    { selected, disabled, dense, disableRipple: _disableRipple, className, onClick, onKeyDown, style, ...rest },
    ref
  ) => {
    /** Activate like a native button, keeping the modifiers so Shift+Enter can open in a dialog. */
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        const { shiftKey, ctrlKey, altKey, metaKey } = e
        e.currentTarget.dispatchEvent(
          new MouseEvent('click', { bubbles: true, cancelable: true, shiftKey, ctrlKey, altKey, metaKey })
        )
      }
      onKeyDown?.(e)
    }
    const classes = [
      styles.item,
      styles.itemButton,
      selected ? styles.itemSelected : undefined,
      dense ? styles.itemDense : undefined,
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <div
        ref={ref}
        role="button"
        tabIndex={disabled ? -1 : 0}
        data-list-row=""
        className={classes}
        aria-selected={selected || undefined}
        aria-disabled={disabled || undefined}
        style={
          disabled
            ? { ...style, opacity: 0.38, pointerEvents: 'none', position: 'relative' }
            : { ...style, position: 'relative' }
        }
        onClick={disabled ? undefined : onClick}
        onKeyDown={disabled ? undefined : handleKeyDown}
        {...rest}
      />
    )
  }
)

ListItemButton.displayName = 'ListItemButton'

/** MenuList -- an alias for List used in menu contexts */
export interface MenuListProps extends HTMLAttributes<HTMLUListElement> {
  dense?: boolean
  /** @deprecated Ignored - kept for MUI compat */
  component?: string
  /** @deprecated Ignored - kept for MUI compat */
  autoFocusItem?: boolean
}

export const MenuList = forwardRef<HTMLElement, MenuListProps>(
  ({ dense, component = 'ul', autoFocusItem: _autoFocusItem, className, ...rest }, ref) => {
    const classes = [styles.list, dense ? styles.dense : undefined, className].filter(Boolean).join(' ')

    return createElement(component, { ref, className: classes, role: 'menu', ...rest })
  }
)

MenuList.displayName = 'MenuList'

/* -------------------------------------------------------------------------- */
/* Disclosure — inline accordion within a List                                */
/*                                                                            */
/* WAI-ARIA Disclosure pattern for an always-visible nav list. Renders one    */
/* <li> of the parent List holding the trigger, a real                        */
/* <button aria-expanded aria-controls>, and the content, a nested <ul>.      */
/* Content children are <li> rows wrapping a ListItemButton. Plain list       */
/* semantics, no menu roles: every row is a Tab stop. One level only —        */
/* multi-level disclosure is intentionally unsupported.                       */
/*                                                                            */
/* Single-child promotion (collapsing a one-item group into a plain row) is   */
/* the CONSUMER's responsibility, not this primitive's.                       */
/* -------------------------------------------------------------------------- */

interface DisclosureContextValue {
  open: boolean
  toggle: () => void
  setOpen: (next: boolean) => void
  triggerId: string
  contentId: string
  triggerRef: React.RefObject<HTMLButtonElement | null>
  contentRef: React.RefObject<HTMLUListElement | null>
}

const DisclosureContext = createContext<DisclosureContextValue | null>(null)

const useDisclosure = (): DisclosureContextValue => {
  const ctx = useContext(DisclosureContext)
  if (!ctx) {
    throw new Error('DisclosureTrigger and DisclosureContent must be used inside a Disclosure')
  }
  return ctx
}

const readStored = (groupId: string, defaultOpen: boolean): boolean => {
  if (typeof window === 'undefined') {
    return defaultOpen
  }
  const stored = window.localStorage.getItem(groupId)
  if (stored === 'true') {
    return true
  }
  if (stored === 'false') {
    return false
  }
  return defaultOpen
}

const rowSelector = `[${listRowAttr}]`

/** The currently-visible rows: the trigger, plus the child rows when expanded. */
const visibleRows = (ctx: DisclosureContextValue): HTMLElement[] => {
  const rows: HTMLElement[] = []
  if (ctx.triggerRef.current) {
    rows.push(ctx.triggerRef.current)
  }
  if (ctx.open && ctx.contentRef.current) {
    rows.push(...Array.from(ctx.contentRef.current.querySelectorAll<HTMLElement>(rowSelector)))
  }
  return rows
}

export interface DisclosureProps {
  /** Stable key the open/closed state persists under in localStorage. */
  groupId: string
  /** Fallback open state when nothing is stored. Defaults to expanded for discoverability. */
  defaultOpen?: boolean
  /** Controlled open state. When set, the owner holds the state: nothing is read from or written to localStorage. */
  open?: boolean
  /** Called with the requested open state on every toggle, controlled or not. */
  onOpenChange?: (open: boolean) => void
  children?: ReactNode
}

export const Disclosure: FC<DisclosureProps> = ({
  groupId,
  defaultOpen = true,
  open: controlledOpen,
  onOpenChange,
  children,
}) => {
  const generatedId = useId()
  const triggerId = `disclosure-trigger-${generatedId}`
  const contentId = `disclosure-content-${generatedId}`
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const contentRef = useRef<HTMLUListElement | null>(null)

  const isControlled = controlledOpen !== undefined
  const [storedOpen, setOpenState] = useState(() => (isControlled ? false : readStored(groupId, defaultOpen)))
  const open = controlledOpen ?? storedOpen

  const persist = (next: boolean) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(groupId, String(next))
    }
  }

  const setOpen = (next: boolean) => {
    if (!isControlled) {
      setOpenState(next)
      persist(next)
    }
    onOpenChange?.(next)
  }

  const toggle = () => setOpen(!open)

  // Empty-group guard: if the DisclosureContent has no renderable children,
  // render nothing at all rather than an orphan trigger.
  let hasContent = false
  Children.forEach(children, child => {
    if (isValidElement(child) && child.type === DisclosureContent) {
      const contentChildren = (child.props as DisclosureContentProps).children
      if (Children.toArray(contentChildren).length > 0) {
        hasContent = true
      }
    }
  })

  if (!hasContent) {
    return null
  }

  const value: DisclosureContextValue = { open, toggle, setOpen, triggerId, contentId, triggerRef, contentRef }

  return (
    <DisclosureContext.Provider value={value}>
      <li className={styles.disclosure}>{children}</li>
    </DisclosureContext.Provider>
  )
}

export interface DisclosureTriggerProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'onClick'> {
  selected?: boolean
  dense?: boolean
}

export const DisclosureTrigger: FC<DisclosureTriggerProps> = ({
  selected,
  dense,
  className,
  onKeyDown,
  children,
  ...rest
}) => {
  const ctx = useDisclosure()
  const { open, toggle, setOpen, triggerId, contentId, triggerRef } = ctx

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault()
      setOpen(true)
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      setOpen(false)
    } else if (e.key === 'Home') {
      e.preventDefault()
      triggerRef.current?.focus()
    } else if (e.key === 'End') {
      e.preventDefault()
      const rows = visibleRows(ctx)
      rows[rows.length - 1]?.focus()
    }
    onKeyDown?.(e)
  }

  const classes = [
    styles.item,
    styles.itemButton,
    styles.disclosureTrigger,
    selected ? styles.itemSelected : undefined,
    dense ? styles.itemDense : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      ref={triggerRef}
      type="button"
      id={triggerId}
      aria-expanded={open}
      aria-controls={contentId}
      className={classes}
      onClick={toggle}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      {children}
    </button>
  )
}

export interface DisclosureContentProps extends HTMLAttributes<HTMLUListElement> {}

export const DisclosureContent: FC<DisclosureContentProps> = ({ className, onKeyDown, children, ...rest }) => {
  const { open, triggerId, contentId, contentRef, triggerRef } = useDisclosure()

  const handleKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    const target = e.target as HTMLElement
    if (target.hasAttribute(listRowAttr)) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        triggerRef.current?.focus()
      } else if (e.key === 'Home') {
        e.preventDefault()
        triggerRef.current?.focus()
      } else if (e.key === 'End') {
        e.preventDefault()
        const items = contentRef.current?.querySelectorAll<HTMLElement>(rowSelector)
        items?.[items.length - 1]?.focus()
      }
    }
    onKeyDown?.(e)
  }

  const classes = [styles.disclosureContent, open ? undefined : styles.disclosureContentCollapsed, className]
    .filter(Boolean)
    .join(' ')

  return (
    <ul
      ref={contentRef}
      id={contentId}
      aria-labelledby={triggerId}
      className={classes}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      {children}
    </ul>
  )
}
